import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi.js';

const ACTION_LABELS = {
  shift_created: 'Shift Created',
  shift_updated: 'Shift Updated',
  shift_deleted: 'Shift Deleted',
  shift_opened: 'Shift Opened',
  shift_requested: 'Shift Requested',
  request_approved: 'Request Approved',
  request_rejected: 'Request Rejected',
  roster_generated: 'Roster Generated',
  roster_restored: 'Roster Restored',
  roster_published: 'Roster Published',
  roster_locked: 'Roster Locked',
};

const ACTION_COLORS = {
  shift_created: '#16a34a',
  shift_updated: '#2563eb',
  shift_deleted: '#dc2626',
  shift_opened: '#d97706',
  shift_requested: '#7c3aed',
  request_approved: '#16a34a',
  request_rejected: '#dc2626',
  roster_generated: '#0891b2',
  roster_restored: '#7c3aed',
  roster_published: '#16a34a',
  roster_locked: '#6b7280',
};

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, day] = iso.split('-');
  return `${day}/${m}/${y}`;
}

function getDetails(log) {
  const d = log.after_data || log.before_data;
  if (!d) return '—';

  if (log.action === 'roster_generated') {
    const weekLabel = fmtDate(d.week_start) || '?';
    const shiftLabel = d.shift_count ?? '?';
    return `Week of ${weekLabel} · ${shiftLabel} shifts`;
  }
  if (log.action === 'roster_restored') {
    return `Restored from v${d.restored_from_version}${d.label ? ` · ${d.label}` : ''}`;
  }
  if (log.action === 'roster_published' || log.action === 'roster_locked') {
    return `Week of ${fmtDate(d.week_start) || '?'}`;
  }
  if (log.action === 'shift_requested' || log.action === 'request_approved' || log.action === 'request_rejected') {
    const date = d.shift_date || d.date;
    const who = d.employee_name ? ` · ${d.employee_name}` : '';
    return `${fmtDate(date)} ${d.start_time}–${d.end_time}${who}`;
  }
  if (log.action === 'shift_deleted') {
    const who = d.employee_name ? ` · ${d.employee_name}` : ' · Unassigned';
    return `${fmtDate(d.date)} ${d.start_time}–${d.end_time} (${d.role})${who}`;
  }

  // shift_created, shift_updated, shift_opened
  const date = d.date || d.shift_date;
  const who = d.employee_name ? ` · ${d.employee_name}` : '';
  return `${fmtDate(date)} ${d.start_time}–${d.end_time} (${d.role})${who}`;
}

const PAGE_SIZE = 50;

export default function AuditLogPage() {
  const api = useApi();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => {
    load();
  }, [offset, filterAction]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
      if (filterAction) params.set('action', filterAction);
      const data = await api.get(`/audit?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="container">
      <div className="audit-header" style={{ marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0 }}>Audit Log</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
            Record of all roster and shift changes
          </p>
        </div>
        <select
          value={filterAction}
          onChange={e => { setFilterAction(e.target.value); setOffset(0); }}
          className="input"
          style={{ width: 200 }}
        >
          <option value="">All actions</option>
          {Object.entries(ACTION_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No audit log entries yet.</div>
      ) : (
        <>
          <div className="card" style={{ padding: '12px 16px', overflow: 'hidden' }}>
            <div className="table-wrap table-wrap-nostack" style={{ borderRadius: 0, border: 'none', margin: '0 -16px' }}>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>By</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td data-label="Time" style={{ color: 'var(--text-muted)' }}>
                      {new Date(log.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td data-label="Action">
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        background: (ACTION_COLORS[log.action] || '#6b7280') + '18',
                        color: ACTION_COLORS[log.action] || '#6b7280',
                      }}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td data-label="By" style={{ color: 'var(--text-muted)' }}>
                      {log.actor_name || (log.actor_role === 'owner' ? 'Owner' : 'Staff')}
                    </td>
                    <td data-label="Details">
                      <span className="details-text">{getDetails(log)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Page {currentPage} of {totalPages} · {total} entries
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
